import { Badge, Button } from '@fc/ui'
import {
  CalendarDays,
  CheckCircle2,
  HeartHandshake,
  Lock,
  ShieldCheck,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { BrandLockup } from '../components/brand'
import { Pricing } from '../components/pricing'
import { ThemeToggle } from '../components/theme-toggle'

type Feature = { icon: LucideIcon; title: string; body: string }

const features: Feature[] = [
  {
    icon: Users,
    title: 'People & households',
    body: 'A rich directory with households, relationships, custom fields, and dynamic lists that update themselves.',
  },
  {
    icon: HeartHandshake,
    title: 'Teams & groups',
    body: 'Organize ministries and public-facing groups, with leaders who only see what they should.',
  },
  {
    icon: CalendarDays,
    title: 'Events & check-in',
    body: 'Registrations, payments, and secure child check-in with guardian verification.',
  },
  {
    icon: ShieldCheck,
    title: 'Private by design',
    body: 'Every record is isolated per organization at the database layer — data never crosses church boundaries.',
  },
]

const isolation = [
  'Row-Level Security on every table',
  'Scoped roles: Org → Site → Team → Group',
  'Full audit trail on sensitive data',
  'Self-hostable & open source (Apache-2.0)',
]

export function Landing() {
  return (
    <div className="min-h-dvh bg-bg">
      {/* Nav */}
      <header className="sticky top-0 z-20 border-b border-border bg-[color-mix(in_srgb,var(--bg)_80%,transparent)] backdrop-blur-md">
        <div className="mx-auto flex h-[70px] max-w-[1140px] items-center justify-between px-5">
          <BrandLockup />
          <nav className="hidden items-center gap-7 text-sm text-muted md:flex">
            <a href="#features" className="transition-colors hover:text-fg">
              Features
            </a>
            <a href="#pricing" className="transition-colors hover:text-fg">
              Pricing
            </a>
            <a href="#privacy" className="transition-colors hover:text-fg">
              Privacy
            </a>
            <a
              href="https://www.fosschurch.com/"
              className="transition-colors hover:text-fg"
              target="_blank"
              rel="noreferrer"
            >
              FOSS Church
            </a>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="ghost" size="sm">
              <Link to="/login">Sign in</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/app">Open app</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-x-0 -top-40 -z-10 h-[420px] bg-brand-grad-soft blur-3xl"
          aria-hidden
        />
        <div className="mx-auto max-w-[1140px] px-5 py-20 text-center sm:py-28">
          <Badge variant="secondary" className="mb-6">
            <Sparkles className="size-3" />
            Open-source church management
          </Badge>
          <h1 className="mx-auto max-w-3xl font-display text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-6xl">
            Shepherd your church with software that{' '}
            <span className="text-gradient">respects your people</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted">
            FC ChMS brings people, ministries, events, and giving into one secure, multi-tenant
            platform — built for small churches, ready for the largest. Technology in service of the
            Gospel.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg">
              <Link to="/app">
                Explore the demo
                <CheckCircle2 />
              </Link>
            </Button>
            <Button asChild size="lg" variant="secondary">
              <Link to="/login">Sign in</Link>
            </Button>
          </div>
          <p className="mt-4 text-xs text-dim">No pressure, no jargon required.</p>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-[1140px] px-5 py-16">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-xl border border-border bg-card p-6 transition-colors hover:bg-card-hover"
            >
              <div className="mb-4 grid size-11 place-items-center rounded-lg bg-brand-grad-soft text-secondary">
                <f.icon className="size-5" />
              </div>
              <h3 className="font-display text-base font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <Pricing />

      {/* Privacy */}
      <section id="privacy" className="mx-auto max-w-[1140px] px-5 py-16">
        <div className="grid items-center gap-10 rounded-2xl border border-border bg-bg-alt p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <Badge variant="primary" className="mb-4">
              <Lock className="size-3" />
              Security from the start
            </Badge>
            <h2 className="font-display text-3xl font-bold tracking-tight">
              Your members’ data never leaves your walls.
            </h2>
            <p className="mt-4 text-muted">
              Multi-tenancy is enforced in the database itself, not just the app. A single misplaced
              query can’t leak one church’s data to another — the boundary is structural.
            </p>
          </div>
          <ul className="space-y-3">
            {isolation.map((item) => (
              <li key={item} className="flex items-center gap-3 rounded-lg bg-card px-4 py-3">
                <ShieldCheck className="size-5 shrink-0 text-primary" />
                <span className="text-sm font-medium">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-[1140px] flex-col items-center justify-between gap-4 px-5 py-8 text-sm text-dim sm:flex-row">
          <BrandLockup />
          <p>© {new Date().getFullYear()} FOSS Church · Apache-2.0</p>
        </div>
      </footer>
    </div>
  )
}
